import type { PolicyCheck, ScenarioAnalysis } from '@prm/shared';
import { mult, pct } from '../format';
import { PanelHead, StatusPill } from './Panel';

function fmt(value: number, format: PolicyCheck['format']): string {
  if (format === 'pct') return pct(value);
  if (format === 'mult') return mult(value);
  return String(Math.round(value));
}

/**
 * Every policy limit as an actual/limit pair. This is the one place a threshold
 * becomes a verdict, so the breach count here is the same number the decision
 * card and the playbook read — there is exactly one answer to "how many limits is
 * this book outside".
 */
export function PolicyPanel({ scenario }: { scenario: ScenarioAnalysis }) {
  const { policy } = scenario;

  return (
    <section className="panel">
      <PanelHead
        title="Policy checks"
        description="The risk budget. Every threshold in the app reads from this object."
        sub={
          <StatusPill tone={policy.breachCount > 0 ? 'bad' : 'good'}>
            {policy.breachCount} / {policy.checks.length} breached
          </StatusPill>
        }
      />

      <div className="policy-grid">
        {policy.checks.map((c) => (
          <div className={`policy-item ${c.breached ? 'is-breach' : ''}`} key={c.id} title={c.detail}>
            <div className="policy-label">{c.label}</div>
            <div className="policy-value">
              <span className={c.breached ? 'text-bad' : c.measurable ? '' : 'faint'}>
                {c.measurable ? fmt(c.actual, c.format) : '—'}
              </span>
              <span className="policy-limit">
                limit {fmt(c.limit, c.format)}
                {c.measurable ? '' : ' · not measurable'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="footnote">
        Limits are editable under Policy and are saved with the book. Changing one changes every flag, check, gate and
        the decision verdict on the next run — there is no second copy of any of these numbers.
      </div>
    </section>
  );
}

/**
 * The gates that have to clear before adding exposure is a decision rather than
 * a reflex. Each shows what it measured against what it needs.
 */
export function PlaybookPanel({ scenario }: { scenario: ScenarioAnalysis }) {
  const { playbook } = scenario;

  return (
    <section className="panel">
      <PanelHead
        title="Risk-on playbook"
        description="Conditions for adding exposure without turning the book one-way."
        sub={
          <StatusPill tone={playbook.cleared ? 'good' : 'bad'}>{playbook.cleared ? 'CLEARED' : 'BLOCKED'}</StatusPill>
        }
      />

      <div className="trigger-list">
        {playbook.gates.map((g) => (
          <div className="trigger-row" key={g.id}>
            <span className={`trigger-state ${g.passed ? 'is-ready' : 'is-blocked'}`}>{g.passed ? '✓' : '✕'}</span>
            <div style={{ minWidth: 0 }}>
              <div className="row-title">{g.label}</div>
              <p className="row-detail">{g.explanation}</p>
            </div>
            <div className="trigger-metric">
              <strong className={g.passed ? 'text-good' : 'text-bad'}>{g.actual}</strong>
              <span>needs {g.required}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="footnote">{playbook.summary}</div>
    </section>
  );
}
